import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/upr-api";
import DOMPurify from "dompurify";
import { RICH_TEXT_SANITIZE } from "@/lib/rich-text";

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch both lists in parallel so the page renders once.
    Promise.allSettled([
      api.get("/artists").then(({ data }) => setArtists(Array.isArray(data) ? data : [])),
      api.get("/team").then(({ data }) => setTeam(Array.isArray(data) ? data : [])),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="artists-page">
      <div className="upr-eyebrow mb-6"><span className="upr-divider"/>Roster</div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-8xl" style={{ letterSpacing: "-0.035em" }}>
        Artists.
      </h1>
      <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
        The musicians, producers and collectives building Universo Positivo&apos;s sound.
      </p>

      {loading ? (
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(6)].map((_, i) => <div key={i} className="aspect-square animate-pulse" style={{ background: "var(--upr-surface)" }} />)}
        </div>
      ) : (
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="artists-grid">
          {artists.map((a) => (
            <Link to={`/artist/${a.id}`} key={a.id} className="block upr-card-hover group" data-testid={`artist-card-${a.id}`}>
              <div className="upr-cover aspect-square" style={{ border: "1px solid var(--upr-border)" }}>
                <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
              </div>
              <div className="pt-3 flex items-baseline justify-between">
                <div className="font-heading font-extrabold text-xl leading-tight" style={{ letterSpacing: "-0.01em" }}>{a.name}</div>
                <span className="upr-eyebrow" style={{ fontSize: "0.6rem" }}>View</span>
              </div>
              {a.bio && (
                <div
                  className="mt-1 text-sm upr-rich-teaser"
                  style={{ color: "var(--upr-muted)" }}
                  data-testid={`artist-bio-${a.id}`}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.bio, RICH_TEXT_SANITIZE) }}
                />
              )}
            </Link>
          ))}
          {artists.length === 0 && (
            <div className="col-span-full text-sm" style={{ color: "var(--upr-muted)" }}>No artists yet.</div>
          )}
        </div>
      )}

      {/* Team section — same grid + card treatment as Artists, but non-clickable
          because team members don't have detail pages. Sorted server-side by
          display_order ascending. */}
      {team.length > 0 && (
        <section className="mt-20 md:mt-28" data-testid="team-section">
          <div className="upr-eyebrow mb-6"><span className="upr-divider" />Team</div>
          <h2 className="font-heading font-black tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-10" style={{ letterSpacing: "-0.03em" }}>
            The people behind the label.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="team-grid">
            {team.map((m) => (
              <div key={m.id} className="block upr-card-hover group" data-testid={`team-card-${m.id}`}>
                <div className="upr-cover aspect-square" style={{ border: "1px solid var(--upr-border)" }}>
                  {m.photo_url
                    ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                    : <div className="w-full h-full" style={{ background: "var(--upr-surface)" }} />}
                </div>
                <div className="pt-3">
                  <div className="font-heading font-extrabold text-xl leading-tight" style={{ letterSpacing: "-0.01em" }}>{m.name}</div>
                </div>
                {m.bio && (
                  <div
                    className="mt-1 text-sm upr-rich-teaser"
                    style={{ color: "var(--upr-muted)" }}
                    data-testid={`team-bio-${m.id}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.bio, RICH_TEXT_SANITIZE) }}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
